import { useEffect, useRef, useState } from "react";
import { Icon } from "../../components/Icon";
import { browserNotificationPermission } from "./notification-permission";
import type { NotificationPermissionAdapter, NotificationPermissionState } from "./notification-permission";
import { startPushSubscription } from "./push-subscription-handoff";
import type { PushSubscriptionStarter } from "./push-subscription-handoff";

type PermissionCopy = { eyebrow: string; title: string; description: string };
const copy: Record<NotificationPermissionState, PermissionCopy> = {
  default: {
    eyebrow: "Разрешение не запрошено", title: "Получайте напоминания от браузера",
    description: "Разрешите системные уведомления, чтобы не пропустить приближающиеся семейные события.",
  },
  granted: {
    eyebrow: "Разрешение выдано", title: "Системные уведомления доступны",
    description: "Осталось подключить этот браузер к семейным напоминаниям.",
  },
  denied: {
    eyebrow: "Уведомления заблокированы", title: "Разрешение отключено в браузере",
    description: "Откройте настройки сайта в браузере, разрешите уведомления и вернитесь на эту страницу.",
  },
  unsupported: {
    eyebrow: "Не поддерживается", title: "Системные уведомления недоступны",
    description: "Этот браузер не поддерживает уведомления. Напоминания по-прежнему доступны на этой странице.",
  },
};

export function PushPermissionCard({
  permission = browserNotificationPermission,
  onPermissionGranted = startPushSubscription,
}: {
  permission?: NotificationPermissionAdapter;
  onPermissionGranted?: PushSubscriptionStarter;
}) {
  const [permissionState, setPermissionState] = useState(permission.getState);
  const [subscriptionState, setSubscriptionState] = useState<
    "idle" | "pending" | "connected" | "error" | "permission-error"
  >("idle");
  const actionPending = useRef(false);

  useEffect(
    () => permission.subscribe((nextState) => setPermissionState(nextState)),
    [permission],
  );

  const handoff = async () => {
    setSubscriptionState("pending");
    try {
      await onPermissionGranted();
      setSubscriptionState("connected");
    } catch {
      setSubscriptionState("error");
    }
  };

  const connect = async () => {
    if (actionPending.current) {
      return;
    }

    actionPending.current = true;
    await handoff();
    actionPending.current = false;
  };

  const enable = async () => {
    if (actionPending.current) {
      return;
    }

    actionPending.current = true;
    setSubscriptionState("pending");
    let nextState: NotificationPermissionState;
    try {
      nextState = await permission.request();
    } catch {
      setSubscriptionState("permission-error");
      actionPending.current = false;
      return;
    }
    setPermissionState(nextState);

    if (nextState !== "granted") {
      setSubscriptionState("idle");
      actionPending.current = false;
      return;
    }

    await handoff();
    actionPending.current = false;
  };

  const currentCopy = copy[permissionState];
  const canConnect = permissionState === "granted";
  const pending = subscriptionState === "pending";

  return (
    <section
      aria-labelledby="push-permission-title"
      className="mb-6 rounded-[24px] border border-[#E3E1D9] bg-white p-5 shadow-[0_14px_36px_rgba(35,33,25,0.06)] sm:p-6"
    >
      <div className="flex flex-col gap-5 sm:flex-row sm:items-center">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#FFF0DF] text-[#FF7A00]">
          <Icon name="bell" size={23} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#C55E00]">
            {currentCopy.eyebrow}
          </p>
          <h2
            id="push-permission-title"
            className="mt-1 text-xl font-extrabold tracking-[-0.035em] text-[#171714]"
          >
            {currentCopy.title}
          </h2>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-[#77776F]">
            {currentCopy.description}
          </p>
          {subscriptionState === "connected" && (
            <p role="status" className="mt-3 text-sm font-semibold text-[#167A3E]">
              Браузер подключён к системным напоминаниям.
            </p>
          )}
          {subscriptionState === "error" && (
            <p role="alert" className="mt-3 text-sm font-semibold text-[#B91C1C]">
              Разрешение сохранено, но подключить браузер не удалось. Попробуйте ещё раз.
            </p>
          )}
          {subscriptionState === "permission-error" && (
            <p role="alert" className="mt-3 text-sm font-semibold text-[#B91C1C]">
              Браузер не смог открыть запрос разрешения. Попробуйте ещё раз.
            </p>
          )}
        </div>

        {permissionState === "default" && (
          <button
            type="button"
            disabled={pending}
            onClick={() => void enable()}
            className="min-h-11 shrink-0 cursor-pointer rounded-full bg-[#FF7A00] px-6 text-sm font-bold text-white disabled:cursor-wait disabled:bg-[#FDBA74]"
          >
            {pending ? "Подключаем…" : "Разрешить уведомления"}
          </button>
        )}
        {canConnect && subscriptionState !== "connected" && (
          <button
            type="button"
            disabled={pending}
            onClick={() => void connect()}
            className="min-h-11 shrink-0 cursor-pointer rounded-full bg-[#171714] px-6 text-sm font-bold text-white disabled:cursor-wait disabled:bg-[#77776F]"
          >
            {pending
              ? "Подключаем…"
              : subscriptionState === "error"
                ? "Повторить подключение"
                : "Подключить браузер"}
          </button>
        )}
      </div>
    </section>
  );
}
